/**
 * Phase B — Offline-First Mobile Sync · App-layer engine (Slice 1).
 *
 * ARCHITECTURE (user-confirmed):
 *  - ALL API caching + write-queueing lives here at the APP layer (IndexedDB
 *    via `idb`). The Service Worker is intentionally NOT used for API data
 *    (the index.html kill-switch unregisters every SW on each load to prevent
 *    the historical "blank page after deploy" bug — see Iter 63).
 *  - Reads use cache-then-network (`cachedGet`): show cached data instantly,
 *    refresh from network when reachable.
 *  - Writes use queue-then-sync: when offline, the payload (+ base64 photos)
 *    is stored in `offline_queue` and flushed when connectivity returns.
 *
 * Two object stores:
 *   • offline_queue   — pending write jobs ({id, feature, label, payload, photos[], ...})
 *   • reference_data  — cached GET responses keyed by a logical name.
 */
import { openDB } from 'idb';
import { apiPost, apiUpload, apiGet } from './api';

const DB_NAME = 'ellora-offline';
const DB_VERSION = 1;

/** Hard cap on base64 photos held in IndexedDB across the whole queue, so we
 *  never silently consume all device storage. UI warns as it approaches this. */
export const MAX_OFFLINE_PHOTOS = 20;
export const PHOTO_WARN_THRESHOLD = 15;

/** Human labels for the Sync Status view. Later slices add their feature keys. */
export const FEATURE_LABELS = {
  dpr: 'DPR',
  grn: 'GRN',
  fuel: 'Fuel Log',
  safety_observation: 'Safety Observation',
  near_miss: 'Near-Miss',
  petty_cash: 'Petty Cash',
};

let _dbPromise = null;
function getDB() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('offline_queue')) {
          db.createObjectStore('offline_queue', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('reference_data')) {
          db.createObjectStore('reference_data', { keyPath: 'key' });
        }
      },
    });
  }
  return _dbPromise;
}

/* ─── Change notifications (lets indicators across the app stay in sync) ─── */
const QUEUE_CHANGED = 'ellora-offline-queue-changed';
function emitChange() {
  try { window.dispatchEvent(new Event(QUEUE_CHANGED)); } catch { /* SSR/no-window */ }
}
export const onQueueChange = (cb) => {
  window.addEventListener(QUEUE_CHANGED, cb);
  return () => window.removeEventListener(QUEUE_CHANGED, cb);
};

/* ───────────────────────── Reference-data cache ───────────────────────── */
export async function cacheReference(key, data) {
  const db = await getDB();
  await db.put('reference_data', { key, data, cached_at: new Date().toISOString() });
}
export async function getReference(key) {
  const db = await getDB();
  const rec = await db.get('reference_data', key);
  return rec ? rec.data : null;
}

/** Cache-then-network read. Returns {data, fromCache}. On network failure,
 *  falls back to the last cached copy; throws only if nothing is cached. */
export async function cachedGet(key, path, params) {
  try {
    const data = await apiGet(path, params);
    await cacheReference(key, data);
    return { data, fromCache: false };
  } catch (e) {
    const cached = await getReference(key);
    if (cached != null) return { data: cached, fromCache: true };
    throw e;
  }
}

/* ──────────── Read-only pre-caching (Slice 5) — 4-hour TTL ─────────────── */
const REF_TTL_MS = 4 * 60 * 60 * 1000;

async function _isFresh(key, ttl = REF_TTL_MS) {
  const db = await getDB();
  const rec = await db.get('reference_data', key);
  if (!rec || !rec.cached_at) return false;
  return (Date.now() - new Date(rec.cached_at).getTime()) < ttl;
}

/** Fetch + cache a reference list, but skip the network call if a fresh
 *  (within TTL) copy already exists. Fully silent — never surfaces errors. */
async function prefetchReference(key, path, ttl = REF_TTL_MS) {
  try {
    if (await _isFresh(key, ttl)) return;
    await cacheReference(key, await apiGet(path));
  } catch { /* offline / endpoint missing — ignore */ }
}

/** Reference lists used by the offline forms. Keys MUST match the keys the
 *  forms read via cachedGet() so the cache is shared (one fetch, all forms). */
const PREFETCH_TARGETS = [
  ['material-master', '/material-master?limit=500'],
  ['employees', '/hr/employees'],
  ['petty-cash-categories', '/petty-cash/categories'],
  ['petty-cash-assignees', '/petty-cash/assignees'],
  ['open-pos', '/procurement/po?status=Approved&limit=200'],
  ['vendors', '/vendors?limit=200'],
];

/** Invisible background prefetch — call on MobileHome mount when online. */
export async function prefetchOfflineReferenceData() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  await Promise.all(PREFETCH_TARGETS.map(([k, p]) => prefetchReference(k, p)));
}

/* ──────────────────────── Last-synced timestamp ───────────────────────── */
const LAST_SYNCED_KEY = 'meta:last_synced_at';
export async function getLastSyncedAt() {
  return getReference(LAST_SYNCED_KEY);
}
async function setLastSyncedAt(iso) {
  await cacheReference(LAST_SYNCED_KEY, iso);
}

/* ─────────────────────────────── Queue ────────────────────────────────── */
export async function enqueue(feature, { label, payload, photos = [] } = {}) {
  const db = await getDB();
  const job = {
    id: `${feature}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    feature,
    label: label || FEATURE_LABELS[feature] || feature,
    payload: payload || {},
    photos: photos || [],
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
  };
  await db.put('offline_queue', job);
  emitChange();
  return job;
}

export async function getQueue() {
  const db = await getDB();
  const all = await db.getAll('offline_queue');
  return all.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
}

export async function removeJob(id) {
  const db = await getDB();
  await db.delete('offline_queue', id);
  emitChange();
}

export async function queueCount() {
  const db = await getDB();
  return db.count('offline_queue');
}

/** {total, byFeature:{label:count}, jobs[]} — feature-typed for the Sync view. */
export async function queueSummary() {
  const jobs = await getQueue();
  const byFeature = {};
  for (const j of jobs) {
    const lbl = j.label || FEATURE_LABELS[j.feature] || j.feature;
    byFeature[lbl] = (byFeature[lbl] || 0) + 1;
  }
  return { total: jobs.length, byFeature, jobs };
}

/** Count base64 photos currently held across the whole queue (for the cap). */
export async function totalQueuedPhotos() {
  const jobs = await getQueue();
  return jobs.reduce((s, j) => s + ((j.photos && j.photos.length) || 0), 0);
}

/** Free photo slots remaining before the global cap. Forms check this before
 *  queueing so the device-storage cap is enforced ACROSS all features. */
export async function remainingPhotoSlots() {
  return Math.max(0, MAX_OFFLINE_PHOTOS - (await totalQueuedPhotos()));
}

/* ─────────────────────────── Sync handlers ────────────────────────────── */
function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(',');
  const mime = (meta.match(/data:(.*?);/) || [])[1] || 'image/jpeg';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

/** Per-feature sync logic. Each handler MUST throw on failure so the job is
 *  retained for the next attempt. Add later slices' features here. */
const HANDLERS = {
  // GRN (Goods Receipt Note) — non-PO mobile receipt. Photos stored as base64
  // data-URLs in photos[]; the backend already accepts base64 images inline
  // so no separate upload step is needed.
  grn: async (job) => {
    const payload = {
      ...job.payload,
      offline_queued_at: job.createdAt,
    };
    // Map indexed photos to the expected field names
    const photoFieldMap = {
      invoice_check: 'invoice_check_image',
      quantity_check: 'quantity_check_image',
      quality_check: 'quality_check_image',
    };
    for (const ph of job.photos || []) {
      const field = photoFieldMap[ph.slot] || ph.slot;
      if (field && ph.dataUrl) payload[field] = ph.dataUrl;
    }
    await apiPost('/grn/non-po', payload);
  },

  dpr: async (job) => {
    const photoRefs = [];
    for (const ph of job.photos || []) {
      const fd = new FormData();
      fd.append('file', dataUrlToBlob(ph.dataUrl), ph.name || 'dpr-photo.jpg');
      const r = await apiUpload('/upload', fd);
      photoRefs.push({ id: r.id, name: r.original_filename || ph.name });
    }
    await apiPost('/dpr', {
      ...job.payload,
      photos: [...((job.payload && job.payload.photos) || []), ...photoRefs],
      offline_queued_at: job.createdAt,
    });
  },

  // Safety observation — photos are inline base64 (data URLs). The backend
  // decodes/compresses them server-side; no separate upload step.
  safety_observation: async (job) => {
    await apiPost('/safety/observations', {
      ...job.payload,
      photos_base64: job.photos || [],
      offline_queued_at: job.createdAt,
    });
  },

  // Near-miss — same inline-base64 contract.
  near_miss: async (job) => {
    await apiPost('/safety/near-miss', {
      ...job.payload,
      photos_base64: job.photos || [],
      offline_queued_at: job.createdAt,
    });
  },

  // Petty cash — single bill image carried inline as raw base64 (no data-URL
  // prefix), mime separate. The server runs the fraud scan asynchronously.
  petty_cash: async (job) => {
    const payload = { ...job.payload, offline_queued_at: job.createdAt };
    const bill = (job.photos || [])[0];
    if (bill && bill.dataUrl) {
      payload.bill_image_base64 = bill.dataUrl.includes(',') ? bill.dataUrl.split(',')[1] : bill.dataUrl;
      payload.bill_image_mime = bill.mime || 'image/jpeg';
    }
    await apiPost('/petty-cash/requests', payload);
  },
};

let _syncing = false;

/** Flush the queue in FIFO order. Idempotent — concurrent callers no-op.
 *  Returns {synced, failed}. */
export async function syncQueue() {
  if (_syncing) return { synced: 0, failed: 0, skipped: true };
  _syncing = true;
  let synced = 0;
  let failed = 0;
  try {
    const jobs = await getQueue();
    for (const job of jobs) {
      const handler = HANDLERS[job.feature];
      if (!handler) continue; // unknown feature — leave it for a future build
      try {
        await handler(job);
        await removeJob(job.id);
        synced++;
      } catch (e) {
        failed++;
        const db = await getDB();
        const updated = {
          ...job,
          attempts: (job.attempts || 0) + 1,
          lastError: e?.response?.data?.detail || e?.message || 'Sync failed',
        };
        await db.put('offline_queue', updated);
        // Auth failure → token is dead; stop hammering the server.
        if (e?.response?.status === 401) break;
      }
    }
  } finally {
    _syncing = false;
    if (synced > 0) { try { await setLastSyncedAt(new Date().toISOString()); } catch { /* ignore */ } }
    emitChange();
  }
  return { synced, failed };
}

/* Spec-named aliases (Slice 5 brief uses these names). */
export const getQueueCount = queueCount;
export const processQueue = syncQueue;
